/**
 * Firestore database hooks and utilities for managing timestamps.
 * This module provides generic functions to standardize CREATED_AT and UPDATED_AT
 * timestamp management across all Firestore operations.
 *
 * @module db-hooks
 */

import firebase from 'firebase';

/**
 * Extends a data object with CREATED_AT timestamp only.
 * Used for creating new documents.
 *
 * @template T - The type of the data object
 * @param {T} data - The data object to extend with creation timestamp
 * @returns {T & { CREATED_AT: firebase.firestore.Timestamp }}
 *
 * @example
 * const campaignData = { name: 'Summer Sale', budget: 1000 };
 * const dataWithTimestamps = extendWithCreatedAt(campaignData);
 * // Result: { name: 'Summer Sale', budget: 1000, CREATED_AT: Timestamp }
 */
export function extendWithCreatedAt<T extends object>(
  data: T
): T & { CREATED_AT: firebase.firestore.Timestamp } {
  const now = firebase.firestore.Timestamp.now();
  return {
    ...data,
    CREATED_AT: now,
  };
}

/**
 * Extends a data object with only UPDATED_AT timestamp.
 * Used for updating existing documents.
 *
 * @template T - The type of the data object
 * @param {T} data - The data object to extend with update timestamp
 * @returns {T & { UPDATED_AT: firebase.firestore.Timestamp }}
 *
 * @example
 * const updateData = { budget: 1500 };
 * const dataWithTimestamp = extendWithUpdatedAt(updateData);
 * // Result: { budget: 1500, UPDATED_AT: Timestamp }
 */
export function extendWithUpdatedAt<T extends object>(
  data: T
): T & { UPDATED_AT: firebase.firestore.Timestamp } {
  const now = firebase.firestore.Timestamp.now();
  return {
    ...data,
    UPDATED_AT: now,
  };
}

/**
 * Creates a new document using .set() with a DocumentReference.
 * Adds only CREATED_AT timestamp.
 *
 * @template T - The type of the document data
 * @param {firebase.firestore.DocumentReference} docRef - The Firestore document reference
 * @param {T} data - The data to create
 * @returns {Promise<firebase.firestore.DocumentReference>} The document reference
 *
 * @example
 * const docRef = FirebaseAppFirestore.collection('campaigns').doc();
 * await createDocumentRef(docRef, { name: 'New Campaign' });
 * 
 * // Or with a specific ID:
 * const userDocRef = FirebaseAppFirestore.collection('users').doc(userUID);
 * await createDocumentRef(userDocRef, userData);
 */
export async function createDocumentRef<T extends object>(
  docRef: firebase.firestore.DocumentReference,
  data: T
): Promise<firebase.firestore.DocumentReference> {
  const newData = extendWithCreatedAt(data);
  await docRef.set(newData);
  return docRef;
}

/**
 * Overwrites an existing document using .set() with a DocumentReference.
 * Adds only UPDATED_AT timestamp since the document already exists.
 *
 * @template T - The type of the document data
 * @param {firebase.firestore.DocumentReference} docRef - The Firestore document reference
 * @param {T} data - The data to set
 * @returns {Promise<firebase.firestore.DocumentReference>} The document reference
 *
 * @example
 * const docRef = FirebaseAppFirestore.collection('campaigns').doc(campaignId);
 * await setDocumentRef(docRef, updatedCampaignData);
 */
export async function setDocumentRef<T extends object>(
  docRef: firebase.firestore.DocumentReference,
  data: T
): Promise<firebase.firestore.DocumentReference> {
  const updatedData = extendWithUpdatedAt(data);
  await docRef.set(updatedData);
  return docRef;
}

/**
 * Updates an existing document using .update() with a DocumentReference.
 * Adds only UPDATED_AT timestamp.
 *
 * @template T - The type of the document data
 * @param {firebase.firestore.DocumentReference} docRef - The Firestore document reference
 * @param {T} data - The data to update
 * @returns {Promise<firebase.firestore.DocumentReference>} The document reference
 *
 * @example
 * const docRef = FirebaseAppFirestore.collection('campaigns').doc(campaignId);
 * await updateDocumentRef(docRef, { status: 'completed' });
 */
export async function updateDocumentRef<T extends object>(
  docRef: firebase.firestore.DocumentReference,
  data: T
): Promise<firebase.firestore.DocumentReference> {
  const updatedData = extendWithUpdatedAt(data);
  await docRef.update(updatedData);
  return docRef;
}